// Detail zastávky — „Noční kokpit". Tři stavy dle prototypu:
//   waiting (nová)  → příjezd|okno + navigace, CTA „Jedu k zákazníkovi"
//   started (jedu)  → totéž, CTA „Jsem na místě", malá Marná jízda
//   arrived (na místě) → materiál s podpisem + pečetě se slovními stavy +
//                        sken, CTA „Dokončit zastávku" (zamčené), Marná jízda
// Veškerá logika (progress store, odvody s podpisy, offline fronta, skener,
// materiál) beze změny — přestavěná je jen prezentační vrstva.

import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Geolocation } from "@capacitor/geolocation";
import { IonContent, IonPage, IonSpinner, useIonAlert } from "@ionic/react";

import { stopKey, materialKey, normalizeSeal } from "@routera/shared";
import { api, zasilkyMaterial, odvodApi, type ZasilkaMaterial, type OdvodExpectedJbo } from "../lib/api";
import { SignaturePad, type SignatureResult } from "../components/SignaturePad";
import { openNavigation } from "../lib/navigation";
import { useShiftStore } from "../store/shiftStore";
import { useStopProgressStore } from "../store/stopProgressStore";
import { useStornoAckStore } from "../store/stornoAckStore";
import { refreshRouteAfterStop } from "../lib/refresh";
import { BarcodeScanner } from "../components/BarcodeScanner";
import { ThemeToggle } from "../components/ThemeToggle";
import { pauseMinutes, shiftHhMm } from "../lib/pauseEta";

type OdvodReseni =
  | { akce: "ok" }
  | { akce: "swap"; nova: string; jmeno: string; podpis: string }
  | { akce: "chybi"; jmeno: string; podpis: string };

type PadCtx =
  | { typ: "swap"; jboId: string; nova: string }
  | { typ: "chybi"; jboId: string }
  | { typ: "navic"; pecet: string }
  | { typ: "material" };

const FUTILE_REASONS = [
  { value: "closed", label: "Zavřeno" },
  { value: "no-bag", label: "Odvod nepřipraven" },
  { value: "no-one", label: "Nikdo nebyl přítomen" },
  { value: "other", label: "Jiné" },
];

const hhmmToMin = (v?: string | null): number | null => {
  const m = /^(\d{1,2}):(\d{2})$/.exec((v ?? "").trim());
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

const NavArrow = () => (
  <svg viewBox="0 0 24 24"><path d="M12 2l7 19-7-4-7 4 7-19z" /></svg>
);

/** Minuty dne → „11:55". */
const fmtHM = (m: number) => {
  const t = ((Math.floor(m) % 1440) + 1440) % 1440;
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`;
};

/** Aktuální čas v minutách dne (se sekundovým zlomkem — plynulý odpočet). */
const nowMinOfDay = () => {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes() + d.getSeconds() / 60;
};

const PencilIcon = () => (
  <svg viewBox="0 0 24 24" style={{ width: 15, height: 15, stroke: "currentColor", fill: "none", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" }}>
    <path d="M17 3a2.8 2.8 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
  </svg>
);

const ScanIcon = () => (
  <svg style={{ width: 19, height: 19, verticalAlign: -4 }} viewBox="0 0 24 24">
    <path d="M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z" />
    <circle cx="12" cy="14" r="3.4" />
  </svg>
);

export function StopDetailScreen() {
  const history = useHistory();
  const location = useLocation();
  const rid = new URLSearchParams(location.search).get("rid");
  const { idx } = useParams<{ idx: string }>();
  const stopIndex = parseInt(idx, 10);
  const [presentAlert] = useIonAlert();

  const shift = useShiftStore((s) => s.shift);
  const setShift = useStopProgressStore((s) => s.setShift);
  const startStop = useStopProgressStore((s) => s.startStop);
  const arriveStop = useStopProgressStore((s) => s.arriveStop);
  const undoLastStep = useStopProgressStore((s) => s.undoLastStep);
  const pickup = useStopProgressStore((s) => s.pickup);
  const markFutile = useStopProgressStore((s) => s.markFutile);
  const odvodEvent = useStopProgressStore((s) => s.odvodEvent);
  const pinLocation = useStopProgressStore((s) => s.pinLocation);
  const statusOf = useStopProgressStore((s) => s.statusOf);

  const ack = useStornoAckStore((s) => s.ack);
  const isAcked = useStornoAckStore((s) => s.isAcked);

  const [sealNumbers, setSealNumbers] = useState<string[]>([]);
  const [sealInput, setSealInput] = useState<string>("");
  const [scannerOpen, setScannerOpen] = useState(false);
  const [acting, setActing] = useState(false);
  const [futileSheetOpen, setFutileSheetOpen] = useState(false);
  // Upřesnění polohy pobočky: řidič stojí na místě → jeho GPS = přesná poloha
  // pro příští navigaci (ukládá se přes pinLocation event).
  const [pinState, setPinState] = useState<"idle" | "loading" | "done" | "error">("idle");
  // Živé hodnoty (dle design handoffu): ETA/km/min se přepočítávají z reálného
  // času — sekundový tik jen na téhle obrazovce.
  const progressAll = useStopProgressStore((s) => s.progress);
  const [nowMin, setNowMin] = useState(nowMinOfDay);
  useEffect(() => {
    const t = setInterval(() => setNowMin(nowMinOfDay()), 1000);
    return () => clearInterval(t);
  }, []);
  const [materialPredani, setMaterialPredani] = useState<SignatureResult | null>(null);
  const [odvodReseni, setOdvodReseni] = useState<Record<string, OdvodReseni>>({});
  const [odvodNavic, setOdvodNavic] = useState<Array<{ pecet: string; ohlaska_id: string; jmeno: string; podpis: string }>>([]);
  const [padCtx, setPadCtx] = useState<PadCtx | null>(null);

  useEffect(() => {
    if (shift?.id) setShift(shift.id);
  }, [shift?.id, setShift]);

  // Po dokončení chceme čerstvou trasu, ale až při odchodu z obrazovky
  // (zastávka se hledá indexem — přeskládání pod rukama by ukázalo jinou).
  const routeDirtyRef = useRef(false);
  useEffect(() => () => {
    if (routeDirtyRef.current) refreshRouteAfterStop();
  }, []);

  const qc = useQueryClient();
  const { data: routes = [], isLoading } = useQuery({
    queryKey: ["mobile", "route", "today"],
    queryFn: api.route.today,
  });

  const { data: zmaterial = [] } = useQuery<ZasilkaMaterial[]>({
    queryKey: ["mobile", "zasilky", "material"],
    queryFn: zasilkyMaterial.list,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  });

  // Sken jde přes ref — callback musí zůstat stabilní kvůli kameře.
  const scanHandlerRef = useRef<(code: string) => void>((code) => {
    setSealNumbers((prev) => prev.includes(code) ? prev : [...prev, code]);
  });
  const handleScan = useCallback((code: string) => {
    scanHandlerRef.current(code);
  }, []);

  const route = rid ? (routes.find((r) => r.route_id === rid) ?? routes[0]) : routes[0];
  const backUrl = rid ? `/route?rid=${rid}` : "/route";
  const stop = route && Number.isFinite(stopIndex) ? (route.stops[stopIndex] as any) : null;

  // Ohlášený odvod z portálu — plánové zastávky nesou klienta v `sheet`,
  // ruční plány nemají ani jedno → stačí branch_id (kolize hlídá server).
  const stopClient: string = String(stop?.client ?? stop?.sheet ?? "");
  const { data: odvodExpected = [] } = useQuery<OdvodExpectedJbo[]>({
    queryKey: ["mobile", "odvod", "expected", stopClient, stop?.branch_id ?? ""],
    queryFn: () => odvodApi.expected(stopClient, stop!.branch_id),
    enabled: !!stop?.branch_id && !stop?.storno && !stop?.is_unloading && !stop?.collection,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  // Km + doba přejezdu z lokálního OSRM: předchozí nestornovaná zastávka
  // s koordináty (u první zastávky depo) → tahle pobočka.
  const _mLat = typeof stop?.lat === "number" ? stop.lat : null;
  const _mLng = typeof stop?.lng === "number" ? stop.lng : null;
  let originCoords: { lat: number; lng: number } | null = null;
  if (route && stop) {
    for (let i = stopIndex - 1; i >= 0; i--) {
      const p = route.stops[i] as any;
      if (!p?.storno && typeof p?.lat === "number" && typeof p?.lng === "number" && (p.lat || p.lng)) {
        originCoords = { lat: p.lat, lng: p.lng };
        break;
      }
    }
    if (!originCoords && route.depot_lat != null && route.depot_lng != null) {
      originCoords = { lat: route.depot_lat, lng: route.depot_lng };
    }
  }
  const { data: legLine } = useQuery({
    queryKey: ["mobile", "route-line", originCoords?.lat, originCoords?.lng, _mLat, _mLng],
    queryFn: () => api.route.line(originCoords!.lat, originCoords!.lng, _mLat!, _mLng!),
    enabled: !!originCoords && _mLat != null && _mLng != null && !stop?.storno,
    staleTime: 10 * 60_000,
    refetchOnWindowFocus: false,
  });
  const legKm = legLine?.km ?? null;
  const legMin = legLine?.min ?? null;

  if (isLoading) {
    return (
      <IonPage>
        <IonContent scrollY={false}>
          <div className="ck scenebg" style={{ height: "100%" }}>
            <div style={{ textAlign: "center", padding: 60 }}><IonSpinner name="crescent" /></div>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  if (!stop) {
    return (
      <IonPage>
        <IonContent scrollY={false}>
          <div className="ck scenebg" style={{ height: "100%" }}>
            <div className="pad-x">
              <h1>Zastávka nenalezena</h1>
            </div>
            <div className="bottom">
              <button className="cta" onClick={() => history.replace(backUrl)}>Zpět na trasu</button>
            </div>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  const stopId: string = (stop as any).stop_key ?? stopKey(stop as any);
  const stopDzId: string | undefined = (stop as any).dz_id || undefined;
  const stopMatKey = materialKey({ client: (stop as any).client, branch_id: stop.branch_id });
  const stopMaterials = zmaterial.filter((m) => {
    if (m.stav !== "predano") return false;
    if (stopDzId && m.den_zastavka_id) return m.den_zastavka_id === stopDzId;
    return materialKey({ client: m.klient ?? "", branch_id: m.branch_id ?? "" }) === stopMatKey;
  });
  const isMarked = stopMaterials.length > 0;
  const materialSeals = stopMaterials.flatMap((m) => m.jednotky.flatMap((j) => j.pecete));

  const maOhlasku = odvodExpected.length > 0;
  const odvodVyreseno = odvodExpected.every((e) => !!odvodReseni[e.id]);
  const canComplete = (!isMarked || !!materialPredani) && (!maOhlasku || odvodVyreseno);

  const lat = typeof stop.lat === "number" ? stop.lat : null;
  const lng = typeof stop.lng === "number" ? stop.lng : null;
  const stopCoords = lat != null && lng != null ? { lat, lng } : undefined;
  const windowFrom = stop.window_from || null;
  const windowTo = stop.window_to || null;
  const phone = stop.phone || null;
  const note = stop.note || null;
  const isStorno = !!stop.storno;
  const isTemp = !!stop.temp;
  const isUnloading = !!stop.is_unloading;
  const status = statusOf(stopId);
  const stornoAcked = isStorno && isAcked(stopId);

  // ETA posunutá o bezpečnostní pauzu.
  const pauseMin = shift && !shift.end_ts ? pauseMinutes(shift.id) : 0;
  const eta = stop.eta ? shiftHhMm(String(stop.eta), pauseMin) : null;
  // Živý přejezd dle design handoffu: naplánováno → ETA = teď + jízda (živě);
  // na cestě → ETA zafixovaná na (odjezd + jízda), km/min lineárně klesají k 0.
  // Bez OSRM dat fallback na serverovou ETA a bez řádku km/min.
  const startedAt = progressAll[stopId]?.startedAt ?? null;
  let etaLiveMin: number | null = null;
  let remainMin: number | null = null;
  let remainKm: number | null = null;
  if (legMin != null && legKm != null) {
    if (status === "started" && startedAt) {
      const d = new Date(startedAt);
      const departMin = d.getHours() * 60 + d.getMinutes() + d.getSeconds() / 60;
      etaLiveMin = departMin + legMin;
      remainMin = Math.max(0, etaLiveMin - nowMin);
    } else {
      etaLiveMin = nowMin + legMin;
      remainMin = legMin;
    }
    remainKm = legKm * (remainMin / legMin);
  }
  const etaText = etaLiveMin != null ? fmtHM(etaLiveMin) : eta;
  const etaLate = (() => {
    const e = etaLiveMin ?? hhmmToMin(eta), b = hhmmToMin(windowTo);
    return e != null && b != null && e > b;
  })();

  // Pořadí zastávky pro chip v hlavičce (depo/vykládka se nepočítá).
  const countedStops = route ? route.stops.filter((s: any) => !s.storno && !s.is_unloading) : [];
  const orderNum = !stop.storno && !(stop as any).is_unloading ? countedStops.indexOf(stop) + 1 : 0;

  const wrapAction = async (fn: () => Promise<void>) => {
    setActing(true);
    try {
      await fn();
    } catch (e: any) {
      presentAlert({ header: "Chyba", message: e?.message ?? "Akce se nepovedla", buttons: ["OK"] });
    } finally {
      setActing(false);
    }
  };

  // Materiál k naložení = zásilky ve stavu 'vytvoreno' — gate před startem.
  const toLoad = zmaterial.filter((m) => m.stav === "vytvoreno");
  const needsMaterialLoad = toLoad.length > 0;

  const handleStart = () => {
    if (needsMaterialLoad) {
      const list = toLoad.map((m) => {
        const s = m.jednotky.flatMap((j) => j.pecete);
        return `•  ${m.branch_id ?? ""} ${(m.klient ?? "").split(" ")[0].toUpperCase()} — ${s.join(", ")}`;
      }).join("\n");
      presentAlert({
        header: "Nejdřív nalož materiál",
        message: `Na dnešní trasu máš naložit materiál (${toLoad.length}):\n\n${list}`,
        buttons: [
          { text: "Zpět", role: "cancel" },
          { text: "Naložit materiál", handler: () => history.push("/shift/material") },
        ],
      });
      return;
    }
    void wrapAction(() => startStop(stopId, stopCoords));
  };
  const handleArrive = () => wrapAction(() => arriveStop(stopId, stopCoords));

  const handleUndo = () => {
    const label =
      status === "started" ? "spuštění jízdy"
      : status === "arrived" ? "potvrzení příjezdu"
      : status === "done" ? "dokončení zastávky"
      : status === "futile" ? "marnou jízdu"
      : "poslední krok";
    presentAlert({
      header: "Vrátit o krok zpět?",
      message: `Zruší se ${label} a zastávka se vrátí o krok zpět. (Pro případ, že jsi klikl špatně.)`,
      buttons: [
        { text: "Ne", role: "cancel" },
        { text: "Vrátit zpět", handler: () => void wrapAction(() => undoLastStep(stopId)) },
      ],
    });
  };

  // ── Ohlášený odvod: párování skenů + scénáře odchylek ─────────────────────
  const pendingExpected = odvodExpected.filter((e) => !odvodReseni[e.id]);

  const odvodCizoPecet = (code: string) => {
    const options: any[] = [{ text: "Zrušit", role: "cancel" }];
    if (pendingExpected.length === 1) {
      options.push({
        text: `Výměna za ${pendingExpected[0].pecet}`,
        handler: () => setPadCtx({ typ: "swap", jboId: pendingExpected[0].id, nova: code }),
      });
    } else if (pendingExpected.length > 1) {
      options.push({
        text: "Výměna za ohlášenou…",
        handler: () => {
          presentAlert({
            header: "Za kterou ohlášenou pečeť?",
            inputs: pendingExpected.map((e) => ({
              type: "radio" as const, label: e.pecet, value: e.id,
            })),
            buttons: [
              { text: "Zrušit", role: "cancel" },
              { text: "Vybrat", handler: (jboId: string) => {
                if (jboId) setPadCtx({ typ: "swap", jboId, nova: code });
              } },
            ],
          });
        },
      });
    }
    options.push({
      text: "JBO navíc",
      handler: () => setPadCtx({ typ: "navic", pecet: code }),
    });
    presentAlert({
      header: `Pečeť ${code} není ohlášená`,
      buttons: options,
    });
  };

  const odvodPrijmiKod = (code: string) => {
    const match = odvodExpected.find((e) => e.pecet === code);
    if (match) {
      if (!odvodReseni[match.id]) {
        setOdvodReseni((r) => ({ ...r, [match.id]: { akce: "ok" } }));
      }
      return;
    }
    if (odvodNavic.some((n) => n.pecet === code)) return;
    odvodCizoPecet(code);
  };

  scanHandlerRef.current = (code: string) => {
    if (maOhlasku && status === "arrived") {
      odvodPrijmiKod(code);
    } else {
      setSealNumbers((prev) => prev.includes(code) ? prev : [...prev, code]);
    }
  };

  const odvodToggle = (e: OdvodExpectedJbo) => {
    const r = odvodReseni[e.id];
    if (!r) {
      setOdvodReseni((x) => ({ ...x, [e.id]: { akce: "ok" } }));
    } else if (r.akce === "ok") {
      setOdvodReseni((x) => { const y = { ...x }; delete y[e.id]; return y; });
    } else {
      presentAlert({
        header: "Zrušit odchylku?",
        message: `Vyřešení JBO ${e.pecet} se vrátí a podpis se zahodí.`,
        buttons: [
          { text: "Ne", role: "cancel" },
          { text: "Zrušit odchylku", handler: () =>
            setOdvodReseni((x) => { const y = { ...x }; delete y[e.id]; return y; }) },
        ],
      });
    }
  };

  const handlePodpisConfirm = (sig: SignatureResult) => {
    if (!padCtx) return;
    if (padCtx.typ === "swap") {
      setOdvodReseni((x) => ({
        ...x, [padCtx.jboId]: { akce: "swap", nova: padCtx.nova, ...sig },
      }));
    } else if (padCtx.typ === "chybi") {
      setOdvodReseni((x) => ({ ...x, [padCtx.jboId]: { akce: "chybi", ...sig } }));
    } else if (padCtx.typ === "navic") {
      const ohlaskaId = odvodExpected[0]?.ohlaska_id;
      if (ohlaskaId) {
        setOdvodNavic((xs) => [...xs, { pecet: padCtx.pecet, ohlaska_id: ohlaskaId, ...sig }]);
      }
    } else if (padCtx.typ === "material") {
      setMaterialPredani(sig);
    }
    setPadCtx(null);
  };

  const handleAddSeal = () => {
    const code = normalizeSeal(sealInput);
    if (!code) return;
    if (maOhlasku && status === "arrived") {
      odvodPrijmiKod(code);
    } else {
      setSealNumbers((prev) => prev.includes(code) ? prev : [...prev, code]);
    }
    setSealInput("");
  };

  const collectedSeals = maOhlasku
    ? [
        ...odvodExpected.flatMap((e) => {
          const r = odvodReseni[e.id];
          if (!r || r.akce === "chybi") return [];
          return [r.akce === "swap" ? r.nova : e.pecet];
        }),
        ...odvodNavic.map((n) => n.pecet),
      ]
    : sealNumbers;

  const handlePickup = () => {
    const seals = collectedSeals;
    const n = seals.length;
    const doPickup = () => void wrapAction(async () => {
      let gps: { lat: number; lng: number } | undefined = stopCoords;
      try {
        const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: false, timeout: 4000 });
        gps = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      } catch { /* fallback stopCoords */ }

      if (maOhlasku) {
        const jboPayload = [
          ...odvodExpected.map((e) => {
            const r = odvodReseni[e.id];
            if (!r) return null;
            if (r.akce === "ok") return { id: e.id, akce: "ok" };
            if (r.akce === "swap")
              return { id: e.id, akce: "swap", nova_pecet: r.nova, jmeno: r.jmeno, podpis: r.podpis };
            return { id: e.id, akce: "chybi", jmeno: r.jmeno, podpis: r.podpis };
          }).filter(Boolean),
          ...odvodNavic.map((x) => ({
            akce: "navic", pecet: x.pecet, ohlaska_id: x.ohlaska_id,
            jmeno: x.jmeno, podpis: x.podpis,
          })),
        ];
        await odvodEvent(stopId, {
          client: stopClient, branch_id: stop.branch_id ?? "",
          jbo: jboPayload, ...(gps ?? {}),
        });
        void qc.invalidateQueries({ queryKey: ["mobile", "odvod", "expected"] });
      }

      await pickup(stopId, n, seals, {
        name: stop.name ?? undefined,
        client: stop.client ?? undefined,
        address: stop.address ?? undefined,
      }, undefined, stopCoords);
      if (isMarked && materialPredani) {
        await Promise.all(stopMaterials.map((m) =>
          zasilkyMaterial.doruceno(m.id, { ...materialPredani, ...(gps ?? {}) }).catch(() => {})
        ));
        void qc.invalidateQueries({ queryKey: ["mobile", "zasilky", "material"] });
      }
      routeDirtyRef.current = true;
    });

    if (n === 0) {
      presentAlert({
        header: "Žádné pečetě",
        message: "Nepřidal jsi žádnou pečeť. Opravdu chceš zastávku dokončit bez pečetí?",
        buttons: [
          { text: "Zrušit", role: "cancel" },
          { text: "Dokončit bez pečetí", role: "destructive", handler: doPickup },
        ],
      });
      return;
    }
    doPickup();
  };

  const handlePin = async () => {
    if (pinState === "loading" || pinState === "done") return;
    setPinState("loading");
    try {
      const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 10000 });
      await pinLocation(stopId, pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy ?? null);
      setPinState("done");
    } catch {
      setPinState("error");
      setTimeout(() => setPinState("idle"), 3000);
    }
  };

  const confirmFutile = (reason: string) => {
    setFutileSheetOpen(false);
    void wrapAction(async () => {
      await markFutile(stopId, reason);
      routeDirtyRef.current = true;
    });
  };

  const preState = !isStorno && (status === "waiting" || status === "started");
  const onSite = !isStorno && status === "arrived";
  const closedState = status === "done" || status === "futile";

  const titleName = `${stop.branch_id ? `${stop.branch_id} ` : ""}${String(stop.name ?? "Pobočka").toUpperCase()}`;

  return (
    <IonPage>
      <IonContent scrollY={false}>
        <div className="ck scenebg" style={{ height: "100%" }}>
          <div className="top">
            <button className="back" onClick={() => history.replace(backUrl)}>‹</button>
            <div style={{ flex: 1, minWidth: 0 }}>
              <b style={{ fontSize: 19, display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{titleName}</b>
              <span style={{ display: "block", fontSize: 13.5, color: "var(--soft)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {stop.address ?? stop.client ?? ""}
              </span>
            </div>
            {/* Chip pořadí (design handoff) — v pre-stavech místo Zavolat,
                telefon tam žije dole v řádku poznámky */}
            {orderNum > 0 && (
              <span className="ordchip">{orderNum}/{countedStops.length}</span>
            )}
            {phone && !preState && (
              <a className="ghostbtn" style={{ flex: "none", textDecoration: "none" }} href={`tel:${phone}`}>
                <svg viewBox="0 0 24 24"><path d="M5 4h4l2 5-2.5 1.5a12 12 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z" /></svg>
                Zavolat
              </a>
            )}
            <ThemeToggle />
          </div>

          <div className={`stopbody${preState ? " pre" : ""}`}>
            {/* stavové bannery */}
            {status === "done" && (
              <div className="pad-x" style={{ marginTop: 12 }}>
                <div className="card" style={{ borderColor: "rgba(61,214,132,.5)", background: "var(--ok-dim)" }}>
                  <b style={{ color: "var(--ok)" }}>✓ Zastávka dokončena</b>
                </div>
              </div>
            )}
            {status === "futile" && (
              <div className="pad-x" style={{ marginTop: 12 }}>
                <div className="card" style={{ borderColor: "rgba(245,174,69,.5)", background: "var(--warn-dim)" }}>
                  <b style={{ color: "var(--warn)" }}>Marná jízda</b>
                </div>
              </div>
            )}
            {isStorno && (
              <div className="pad-x" style={{ marginTop: 12 }}>
                <div className="card" style={stornoAcked ? undefined : { borderColor: "rgba(242,101,88,.5)", background: "var(--bad-dim)" }}>
                  <b style={{ color: stornoAcked ? "var(--dim)" : "var(--bad)", fontSize: 14.5 }}>
                    {stornoAcked ? "STORNO — odklikáno" : "STORNO"}
                  </b>
                  <div style={{ fontSize: 12.5, color: "var(--soft)", marginTop: 4 }}>
                    {stornoAcked
                      ? "Zastávka byla na trase — svoz se neprovádí."
                      : "Klient oznámil storno. Zastávka je jen informativní — svoz se neprovádí."}
                  </div>
                </div>
              </div>
            )}
            {isTemp && !isStorno && (
              <div className="pad-x" style={{ marginTop: 10 }}>
                <div style={{ fontSize: 12.5, color: "var(--brass)", fontWeight: 600 }}>
                  {isUnloading ? "Vykládací zastávka přidaná dispečerem" : "Nakládací zastávka přidaná dispečerem"}
                </div>
              </div>
            )}
            {note && !preState && (
              <div className="pad-x" style={{ marginTop: 10 }}>
                <div style={{ fontSize: 12.5, color: "var(--brass)", fontStyle: "italic" }}>{note}</div>
              </div>
            )}

            {/* PŘED PŘÍJEZDEM (design handoff): banner → info karta → navigace
                → záměrně prázdný střed → poznámka+telefon */}
            {preState && (
              <>
                {status === "started" && (
                  <div className="enbanner">
                    <NavArrow /> Na cestě k zákazníkovi
                  </div>
                )}
                {(etaText || (windowFrom && windowTo)) && (
                  <div className={`infocard${etaLate ? " late" : ""}`}>
                    <div className="toprow">
                      {etaText && (
                        <div>
                          <div className="eta">~{etaText}</div>
                          <div className="lbl">{etaLate ? "příjezd · po okně!" : "příjezd"}</div>
                        </div>
                      )}
                      {windowFrom && windowTo && (
                        <div style={{ textAlign: "right" }}>
                          <div className="win2">{windowFrom}–{windowTo}</div>
                          <div className="lbl">svozové okno</div>
                        </div>
                      )}
                    </div>
                    {remainKm != null && remainMin != null && (
                      <>
                        <div className="div" />
                        <div className="legrow">
                          <div className="leg">
                            <b>{remainKm < 10 ? remainKm.toFixed(1) : Math.round(remainKm)}</b>
                            <span>km</span>
                          </div>
                          <div className="leg r">
                            <b>{Math.ceil(remainMin)}</b>
                            <span>min jízdy</span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
                {lat != null && (
                  <div className="navrow">
                    <button className="navbtn" style={{ flex: 1 }} onClick={() => openNavigation({ lat: lat!, lng: lng!, label: stop.name }, "mapy")}>
                      <NavArrow /> Mapy.cz
                    </button>
                    <button className="navbtn" style={{ flex: 1 }} onClick={() => openNavigation({ lat: lat!, lng: lng!, label: stop.name }, "waze")}>
                      <NavArrow /> Waze
                    </button>
                  </div>
                )}
                {/* Záměrně prázdný střed — nechává vyniknout pattern pozadí */}
                <div style={{ flex: 1 }} />
                {(note || phone) && (
                  <div className="noterow">
                    <div className="nico"><PencilIcon /></div>
                    <p>
                      {note}
                      {note && phone ? " · " : ""}
                      {phone && (
                        <a href={`tel:${phone.replace(/\s/g, "")}`}>
                          <svg viewBox="0 0 24 24" style={{ width: 12, height: 12, verticalAlign: -1, marginRight: 4, stroke: "currentColor", fill: "none", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" }}>
                            <path d="M5 4h4l2 5-2.5 1.5a12 12 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z" />
                          </svg>
                          {phone}
                        </a>
                      )}
                    </p>
                  </div>
                )}
              </>
            )}

            {/* NA MÍSTĚ: materiál + pečetě + sken */}
            {onSite && (
              <>
                {isMarked && (
                  <div style={{ padding: "0 18px" }}>
                    <button
                      className={`matb${materialPredani ? " ok" : ""}`}
                      onClick={() => {
                        if (materialPredani) {
                          presentAlert({
                            header: "Zrušit podpis předání?",
                            buttons: [
                              { text: "Ne", role: "cancel" },
                              { text: "Zrušit podpis", handler: () => setMaterialPredani(null) },
                            ],
                          });
                        } else {
                          setPadCtx({ typ: "material" });
                        }
                      }}
                    >
                      <span className="ic">
                        <svg style={{ width: 22, height: 22, stroke: "currentColor", fill: "none", strokeWidth: 1.5, strokeLinecap: "round", strokeLinejoin: "round" }} viewBox="0 0 24 24">
                          <path d="M21 8l-9-4.5L3 8v8l9 4.5L21 16V8z" /><path d="M3 8l9 4.5L21 8M12 12.5V20.5" />
                        </svg>
                      </span>
                      <span>
                        <b>{materialPredani ? `Materiál předán — ${materialPredani.jmeno}` : "Předat materiál s podpisem"}</b>
                        {materialSeals.length > 0 && (
                          <small><span className="mono" style={{ color: "var(--soft)" }}>{materialSeals.join(" · ")}</span></small>
                        )}
                      </span>
                    </button>
                  </div>
                )}

                {isUnloading ? null : (
                  <>
                    <div className="seals">
                      <div className="cap" style={{ display: "flex", alignItems: "baseline" }}>
                        {maOhlasku ? "Ohlášený odvod" : "Pečetě"}
                        <span className="spacer" />
                        <span className="mono" style={{ fontSize: 15, color: "var(--ink)", letterSpacing: 0 }}>
                          {maOhlasku
                            ? `${Object.keys(odvodReseni).length} / ${odvodExpected.length}`
                            : `${sealNumbers.length}`}
                        </span>
                      </div>

                      {maOhlasku ? (
                        <>
                          {odvodExpected.map((e) => {
                            const r = odvodReseni[e.id];
                            const cls = !r ? "seal" : r.akce === "ok" ? "seal ok" : r.akce === "swap" ? "seal swap" : "seal";
                            return (
                              <div key={e.id} className={cls} style={{ display: "flex", ...(r?.akce === "chybi" ? { opacity: 0.65 } : {}) }}>
                                <button
                                  onClick={() => odvodToggle(e)}
                                  style={{ flex: 1, display: "flex", alignItems: "center", gap: 10, textAlign: "left", minWidth: 0, padding: 0 }}
                                >
                                  <span className="code">
                                    {r?.akce === "swap" ? (
                                      <>
                                        <s style={{ color: "var(--dim)", fontSize: 14 }}>{e.pecet}</s> → {r.nova}
                                      </>
                                    ) : (
                                      <span style={r?.akce === "chybi" ? { textDecoration: "line-through" } : undefined}>{e.pecet}</span>
                                    )}
                                  </span>
                                  <span className="st" style={!r ? { color: "var(--dim)" } : undefined}>
                                    {!r ? "nenaskenováno"
                                      : r.akce === "ok" ? "✓ převzato"
                                      : r.akce === "swap" ? `změněno · ${r.jmeno}`
                                      : `nepředáno · ${r.jmeno}`}
                                  </span>
                                </button>
                                {!r && (
                                  <button
                                    onClick={() => setPadCtx({ typ: "chybi", jboId: e.id })}
                                    style={{
                                      flexShrink: 0, padding: "6px 10px", borderRadius: 9,
                                      border: "1px solid rgba(242,101,88,.55)", color: "var(--bad)",
                                      fontSize: 12, fontWeight: 700,
                                    }}
                                  >
                                    Nepředáno
                                  </button>
                                )}
                              </div>
                            );
                          })}
                          {odvodNavic.map((x) => (
                            <div key={x.pecet} className="seal extra" style={{ display: "flex" }}>
                              <span className="code">{x.pecet}</span>
                              <span className="st">navíc · {x.jmeno}</span>
                              <button
                                onClick={() => setOdvodNavic((xs) => xs.filter((y) => y.pecet !== x.pecet))}
                                aria-label="Odebrat JBO navíc"
                                style={{ color: "var(--dim)", fontSize: 22, padding: "0 2px", lineHeight: 1 }}
                              >×</button>
                            </div>
                          ))}
                        </>
                      ) : (
                        sealNumbers.map((code) => (
                          <div key={code} className="seal ok" style={{ display: "flex" }}>
                            <span className="code">{code}</span>
                            <span className="st">✓ naskenováno</span>
                            <button
                              onClick={() => setSealNumbers((prev) => prev.filter((c) => c !== code))}
                              aria-label="Smazat pečeť"
                              style={{ color: "var(--dim)", fontSize: 24, padding: "0 4px", lineHeight: 1 }}
                            >×</button>
                          </div>
                        ))
                      )}
                    </div>

                    <div style={{ padding: "12px 18px 0" }}>
                      <button className="scanbtn" onClick={() => setScannerOpen(true)}>
                        <ScanIcon /> &nbsp;Skenovat pečeť
                      </button>
                    </div>

                    {/* ruční zadání (wedge čtečka / nečitelný kód) */}
                    <div className="tkrow" style={{ padding: "10px 18px 0", marginBottom: 0 }}>
                      <div className="tkfield">
                        {/* Plná klávesnice — pečetě obsahují i písmena */}
                        <input
                          value={sealInput}
                          onChange={(e) => setSealInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") handleAddSeal(); }}
                          placeholder="Pečeť ručně…"
                          autoCapitalize="characters"
                          autoCorrect="off"
                          spellCheck={false}
                        />
                      </div>
                      <button
                        className="navbtn"
                        style={{ flex: "none", width: 54, opacity: sealInput.trim() ? 1 : 0.5 }}
                        disabled={!sealInput.trim()}
                        onClick={handleAddSeal}
                      >＋</button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          <div className="bottom">
            {isStorno && !stornoAcked && (
              <button
                className="cta"
                onClick={() => { ack(stopId); history.replace(backUrl); }}
              >
                Rozumím, neprovádět
              </button>
            )}
            {isStorno && stornoAcked && (
              <button className="cta quiet" onClick={() => history.replace(backUrl)}>Zpět na trasu</button>
            )}
            {!isStorno && status === "waiting" && (
              <button className={`cta${acting ? " locked" : ""}`} disabled={acting} onClick={handleStart}>
                Jedu k zákazníkovi
              </button>
            )}
            {!isStorno && status === "started" && (
              <button className={`cta${acting ? " locked" : ""}`} disabled={acting} onClick={() => void handleArrive()}>
                Dorazil jsem na místo
              </button>
            )}
            {onSite && (
              <>
                <button
                  className={`cta${(acting || !canComplete) ? " locked" : ""}`}
                  disabled={acting || !canComplete}
                  onClick={isUnloading
                    ? () => void wrapAction(async () => {
                        await pickup(stopId, 0, [], {
                          name: stop.name ?? undefined,
                          client: stop.client ?? undefined,
                          address: stop.address ?? undefined,
                        }, undefined, stopCoords);
                        routeDirtyRef.current = true;
                      })
                    : handlePickup}
                >
                  Dokončit zastávku
                </button>
                <button className="voidbtn" onClick={() => setFutileSheetOpen(true)}>Marná jízda</button>
              </>
            )}
            {!isStorno && closedState && (
              <button className="cta quiet" onClick={() => history.replace(backUrl)}>Zpět na trasu</button>
            )}
            {!isStorno && (status === "started" || status === "arrived" || closedState) && (
              <div style={{ display: "flex", justifyContent: "center", gap: 22, marginTop: 8 }}>
                {(onSite || closedState) && (
                  <button
                    className="ghostbtn"
                    style={pinState === "done" ? { color: "var(--ok)" } : pinState === "error" ? { color: "var(--bad)" } : undefined}
                    onClick={() => void handlePin()}
                    disabled={pinState === "loading" || pinState === "done"}
                  >
                    <svg viewBox="0 0 24 24"><path d="M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11z" /><circle cx="12" cy="10" r="2.6" /></svg>
                    {pinState === "loading" ? "Zjišťuji GPS…"
                      : pinState === "done" ? "Poloha uložena"
                      : pinState === "error" ? "GPS se nepovedla"
                      : "Upřesnit polohu"}
                  </button>
                )}
                <button className="ghostbtn" onClick={handleUndo} disabled={acting}>
                  <svg viewBox="0 0 24 24"><path d="M3 7v6h6" /><path d="M21 17a9 9 0 0 0-15-6.7L3 13" /></svg>
                  Vrátit o krok zpět
                </button>
              </div>
            )}
          </div>

          {/* Marná jízda — výběr důvodu v dialogu (mini scenebg) */}
          <div className={`ovl dlg${futileSheetOpen ? " on" : ""}`} onClick={() => setFutileSheetOpen(false)}>
            <div className="dlgbox scenebg" onClick={(e) => e.stopPropagation()}>
              <b>Marná jízda?</b>
              <p>Zastávka se označí jako neuskutečněná a dispečink dostane oznámení. Vyber důvod:</p>
              {FUTILE_REASONS.map((r) => (
                <button
                  key={r.value}
                  className="cta quiet"
                  style={{ padding: 14, fontSize: 15 }}
                  onClick={() => confirmFutile(r.value)}
                >
                  {r.label}
                </button>
              ))}
              <div className="dlgrow" style={{ marginTop: 12 }}>
                <button className="no" onClick={() => setFutileSheetOpen(false)}>Zrušit</button>
              </div>
            </div>
          </div>
        </div>
      </IonContent>

      {scannerOpen && (
        <BarcodeScanner
          onScan={handleScan}
          onClose={() => setScannerOpen(false)}
        />
      )}

      {padCtx && (
        <SignaturePad
          title={
            padCtx.typ === "swap" ? `Změna pečeti → ${padCtx.nova}`
            : padCtx.typ === "chybi" ? "JBO nepředáno"
            : padCtx.typ === "navic" ? `JBO navíc ${padCtx.pecet}`
            : "Předání materiálu"
          }
          subtitle="Podepisuje obsluha pobočky"
          onConfirm={handlePodpisConfirm}
          onCancel={() => setPadCtx(null)}
        />
      )}
    </IonPage>
  );
}
